/**
 * Authentication Error Handling Utility
 * 
 * Provides user-friendly error messages for authentication flows,
 * abstracting technical details like status codes from end users
 */

import { AxiosError } from 'axios';

/**
 * Authentication Error Types
 */
export enum AuthErrorType {
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  CONNECTION_ERROR = 'CONNECTION_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * User-friendly Authentication Error
 */
export interface UserFriendlyAuthError {
  type: AuthErrorType;
  message: string;
  translationKey: string;
}

/**
 * Maps technical HTTP errors to user-friendly authentication errors
 * 
 * @param error - The original error from API call
 * @returns User-friendly error object
 */
export const mapAuthError = (error: unknown): UserFriendlyAuthError => {
  // Handle Axios errors
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError;
    
    // 401 Unauthorized - Invalid credentials (don't expose status code)
    if (axiosError.response?.status === 401) {
      return {
        type: AuthErrorType.INVALID_CREDENTIALS,
        message: 'Invalid email or password',
        translationKey: 'auth.invalidCredentials',
      };
    }
    
    // Network/connection errors - no response from server
    if (!axiosError.response) {
      return {
        type: AuthErrorType.CONNECTION_ERROR,
        message: 'Connection failed. Please check your internet connection.',
        translationKey: 'auth.connectionError',
      };
    }
    
    // Server errors (5xx) or other HTTP errors
    if (axiosError.response.status >= 500) {
      return {
        type: AuthErrorType.SERVER_ERROR,
        message: 'Service temporarily unavailable. Please try again later.',
        translationKey: 'auth.serverError',
      };
    }
    
    // Other HTTP errors (4xx except 401)
    return {
      type: AuthErrorType.INVALID_CREDENTIALS,
      message: 'Invalid email or password',
      translationKey: 'auth.invalidCredentials',
    };
  }
  
  // Handle generic Error objects
  if (error instanceof Error) {
    // Check if it's already a user-friendly message (from our services)
    const message = error.message.toLowerCase();
    
    if (message.includes('invalid') || message.includes('unauthorized')) {
      return {
        type: AuthErrorType.INVALID_CREDENTIALS,
        message: 'Invalid email or password',
        translationKey: 'auth.invalidCredentials',
      };
    }
    
    if (message.includes('network') || message.includes('connection')) {
      return {
        type: AuthErrorType.CONNECTION_ERROR,
        message: 'Connection failed. Please check your internet connection.',
        translationKey: 'auth.connectionError',
      };
    }
  }
  
  // Fallback for unknown errors
  return {
    type: AuthErrorType.UNKNOWN_ERROR,
    message: 'Login failed. Please try again.',
    translationKey: 'auth.loginFailed',
  };
};

/**
 * Creates a user-friendly error message for authentication failures
 * 
 * @param error - The original error
 * @param useTranslationKey - Whether to return translation key instead of message
 * @returns User-friendly error message or translation key
 */
export const getUserFriendlyAuthError = (
  error: unknown, 
  useTranslationKey = false
): string => {
  const mappedError = mapAuthError(error);
  return useTranslationKey ? mappedError.translationKey : mappedError.message;
};
